// components/form-ui/social.tsx
"use client";

import { Button } from "@/components/ui/button";
import SigningInOverlay from "@/components/auth/signing-in-overlay";
import { LanguageType } from "@/lib/types";
import { _t } from "@/lib/utils";
import { DEFAULT_LOGIN_REDIRECT } from "@/routes";
import { signIn } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { TfiMicrosoftAlt } from "react-icons/tfi";

enum Providers {
  Microsoft = "microsoft-entra-id",
}

export const Social = ({ lang }: { lang: LanguageType }) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const callbackUrl =
    searchParams.get("callbackUrl") ||
    (lang === LanguageType.AR
      ? `/ar-sa${DEFAULT_LOGIN_REDIRECT}`
      : DEFAULT_LOGIN_REDIRECT);
  // const callbackUrl = searchParams.get("callbackUrl") || DEFAULT_LOGIN_REDIRECT;
  const feedbackParam = searchParams.get("feedback");
  const [error, setError] = useState(searchParams.get("error"));
  const [isLoading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(
    feedbackParam ? decodeURIComponent(feedbackParam) : null
  );

  //catch feedback
  useEffect(() => {
    if (error === "Configuration") {
      setFeedback(_t("auth_error", lang)); // Translate "Authentication error occurred."
    }
  }, [error, lang]);

  const onClick = async () => {
    if (isLoading) return;

    setLoading(true);
    setFeedback(null);

    try {
      // Call the API instead of directly calling clearCookies()
      const response = await fetch("/api/clear-cookies", { method: "GET" });

      if (!response.ok) {
        console.error("Failed to clear cookies:", await response.text());
      }

      // const result =
      signIn(Providers.Microsoft, {
        callbackUrl,
        redirect: true, // ✅ Let `router.replace()` handle the navigation
        prompt: "select_account",
      });

      // if (result?.error) {
      //   setError("result");
      //   setFeedback(_t("sign_in_failed", lang));
      // } else {
      //   console.log("✅ Redirecting to:", result?.url || callbackUrl);
      //   router.replace(result?.url || callbackUrl);
      // }
    } catch (error) {
      console.error("Error during sign-in:", error);
      setFeedback(_t("unexpected_error", lang));
      // Live feedback, 2026-10-02: the overlay below covers the whole
      // screen while isLoading is true -- previously nothing reset it on
      // this path, so a real failure here (e.g. /api/clear-cookies
      // throwing) left the overlay up forever with the error message
      // hidden behind it instead of showing.
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center gap-y-4">
      <SigningInOverlay visible={isLoading} label={_t("employee_login_signing_in", lang)} />
      {feedback && (
        <div
          className={`text-sm text-center mb-2 ${
            feedback.toLowerCase().includes("error")
              ? "text-secondary dark:text-[#EF4444]"
              : "text-green-600: dark:text-[#22C55E]"
          }`}
          aria-live="polite"
        >
          {feedback}
        </div>
      )}
      <Button
        className="w-full h-12 rounded-full text-base gap-x-3 dark:text-buttonPrimaryTextDark bg-buttonPrimaryDark"
        onClick={onClick}
        disabled={isLoading}
      >
        {isLoading ? (
          <AiOutlineLoading className="animate-spin" />
        ) : (
          <TfiMicrosoftAlt />
        )}
        {_t("login_with_microsoft", lang)}
      </Button>
    </div>
  );
};
